import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const alertVariants = cva(
  'rounded-lg border p-4 flex items-start gap-3',
  {
    variants: {
      variant: {
        success: 'bg-success/10 border-success text-success-dark',
        warning: 'bg-warning/10 border-warning text-warning-dark',
        error: 'bg-error/10 border-error text-error-dark',
        info: 'bg-info/10 border-info text-info-dark',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const iconMap = {
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: XCircleIcon,
  info: InformationCircleIcon,
};

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  showIcon?: boolean;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      variant = 'info',
      title,
      children,
      onClose,
      showIcon = true,
      ...props
    },
    ref
  ) => {
    const Icon = iconMap[variant || 'info'];

    return (
      <div
        ref={ref}
        className={alertVariants({ variant, className })}
        role="alert"
        {...props}
      >
        {showIcon && <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />}
        <div className="flex-1">
          {title && (
            <h3 className="mb-1 text-sm font-semibold">{title}</h3>
          )}
          <div className="text-sm">{children}</div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-md p-1 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2"
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';
